import { Game as PhaserGame } from 'phaser';
import { UIEvents, FloorPanelClickedEvent, LiftMoveCompletedEvent, LiftSimulationScene, LiftPanelClickedEvent, g_ActiveSimulationScene } from './ui/LiftSimulationScene';
import { isAvailable as isStorageAvailable, getItem, setItem, STORAGE_KEYS } from './storage/localStorage';
import {
    createMissionSettingsUI,
    removeMissionSettingsUI,
    UIChrome
} from './ui';
import type { MissionConfig } from './types';
import { SimulationState } from './types/simulationState';
import { newSimulation, generateVisitorTimeline, processVisitorTimeline, processVisitors, updateLiftStatus, FutureVisitor, onLiftMoveCompleted, setLiftTargets } from './simulator';
import { EventBus } from './EventBus';
import { CodeSandbox, LUA_TEMPLATES, SetLiftTargetEvent, SimulationEvents, LUA_CODE_SNIPPETS } from './CodeSandbox';
import { CodeEditor, getLuaDefinitions, loadMonacoModules as loadMonacoSubModules } from './CodeEditor';
import { createLiftCallPanel } from './ui/LiftCallPanel';
import { t } from './i18n';

const SIMULATION_TICK_INTERVAL_MILLIS = 1000;

interface GameState {
    phase: 'settings' | 'playing';
    missionConfig: MissionConfig | null;
    simulation: SimulationState | null;
    codeSandbox: CodeSandbox | null;
    codeEditor: CodeEditor | null;
    docEditor: CodeEditor | null;
    generationTimer: number | null;
    visitorTimeline: Record<number, FutureVisitor[]> | null;
    eventQueue: { type: string, data: unknown }[];
    isPaused: boolean;
}

const gameState: GameState = {
    phase: 'settings',
    missionConfig: null,
    simulation: null,
    codeSandbox: null,
    codeEditor: null,
    docEditor: null,
    generationTimer: null,
    visitorTimeline: null,
    eventQueue: [],
    isPaused: false
};

let uiChrome: UIChrome | null = null;
let gameCtx: PhaserGame | null = null;

/**
 * Start the mission with the given config
 */
async function startMission(config: MissionConfig): Promise<void> {
    gameState.missionConfig = config;
    gameState.phase = 'playing';
    gameState.simulation = null;
    gameState.codeSandbox = null;
    gameState.isPaused = false;
    gameState.visitorTimeline = generateVisitorTimeline(gameState.missionConfig);

    const settingsContainer = document.getElementById('settings-container');
    if (settingsContainer) {
        removeMissionSettingsUI(settingsContainer);
        settingsContainer.style.display = 'none';
    }

    const gameContainer = document.getElementById('game-container');
    if (gameContainer) {
        gameContainer.classList.add('game-container');
        gameContainer.style.display = 'grid'; // Grid from main.css
        renderLayout(gameContainer);
        const mainCodeTemplate = LUA_TEMPLATES.MAIN;

        // Load code from storage if available, otherwise use template
        const savedCode = getItem<string>(STORAGE_KEYS.USER_CODE);
        const codeToInitialize = savedCode || mainCodeTemplate;

        gameState.codeEditor = new CodeEditor(document.getElementById('editor-panel')!, codeToInitialize);

        // Initialize Documentation Editor
        fetch('./assets/docs/README.md')
            .then(res => res.text())
            .then(intro => {
                const apiDocs = '```lua\n' + getLuaDefinitions() + '\n```';
                const fullDoc = intro + '\n\n' + apiDocs;
                gameState.docEditor = new CodeEditor(
                    document.getElementById('tab-documentation-panel')!,
                    fullDoc,
                    'markdown',
                    true
                );
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.error("Failed to load documentation:", err);
            });

        const toolbarContainer = document.getElementById('editor-toolbar');
        if (toolbarContainer) {
            setupEditorToolbar(toolbarContainer, mainCodeTemplate);
        }

        setupEditorTabs();
    } else {
        throw new Error('Game container not found');
    }

    initGameRendering(gameState.missionConfig);
}

function stopSimulation(): void {
    stopSimulationTimer();
    gameState.simulation = null;
    gameState.isPaused = false;
    gameState.codeSandbox?.close();
    gameState.codeSandbox = null;
    updateGameRenderingState();
}

async function startSimulation(): Promise<void> {
    stopSimulation();
    const consoleOutput = document.getElementById('console-output');
    if (consoleOutput) consoleOutput.innerHTML = '';

    gameState.simulation = newSimulation(gameState.missionConfig!);
    gameState.codeSandbox = await CodeSandbox.open(gameState.codeEditor?.getCode() || "", simulationLog);
    updateGameRenderingState();

    // call the onInit LUA function
    await gameState.codeSandbox?.onInit(gameState.missionConfig!);
    // start ticking generations
    startSimulationTimer();
}

function startSimulationTimer(): void {
    stopSimulationTimer();
    if (gameState.isPaused) return;
    gameState.generationTimer = setInterval(onTick, SIMULATION_TICK_INTERVAL_MILLIS);
}

async function onTick(): Promise<void> {
    if (gameState.simulation == null || gameState.missionConfig == null) {
        return;
    }

    // Start the new generation
    gameState.simulation.generation++;

    // Process Event Queue
    const events = [...gameState.eventQueue];
    gameState.eventQueue = [];

    const postProcessingCallbacks: (() => Promise<void>)[] = [];

    for (const event of events) {
        switch (event.type) {
            case UIEvents.LIFT_MOVE_COMPLETED:
                const endData = event.data as LiftMoveCompletedEvent;
                onLiftMoveCompleted(gameState.simulation!, endData.liftId, endData.newFloorNumber);
                break;
            case SimulationEvents.SET_LIFT_TARGET:
                const targetData = event.data as SetLiftTargetEvent;
                setLiftTargets(gameState.simulation!, targetData.liftId, targetData.targetFloors);
                break;
        }
    }

    // Set the lift moving/idle status based on targets
    updateLiftStatus(gameState.simulation!,
        (liftId: string, currentFloorNumber: number, targetFloorNumber: number) => {
            simulationLog('Lift Move Started', liftId, currentFloorNumber, targetFloorNumber);
            postProcessingCallbacks.push(async (): Promise<void> => {
                await gameState.codeSandbox?.onLiftMoveStart(liftId, currentFloorNumber, gameState.simulation!);
            });
        },
        (liftId: string, newFloorNumber: number) => {
            simulationLog('Lift Move Completed', liftId, newFloorNumber);
            postProcessingCallbacks.push(async (): Promise<void> => {
                await gameState.codeSandbox?.onLiftMoveEnd(liftId, newFloorNumber, gameState.simulation!);
            });
        }
    );

    // Process new visitors
    if (gameState.visitorTimeline) {
        processVisitorTimeline(gameState.simulation!, gameState.visitorTimeline);
    }

    processVisitors(gameState.simulation!);

    // update UI, game engine and code sandbox.
    uiChrome?.update({ generation: gameState.simulation.generation });
    await gameState.codeSandbox?.onTick(gameState.simulation);

    g_ActiveSimulationScene?.markDirty();

    // Process any post-processing callbacks
    for (const callback of postProcessingCallbacks) {
        await callback();
    }
}

function togglePause(): void {
    if (!gameState.simulation) return;

    gameState.isPaused = !gameState.isPaused;
    if (gameState.isPaused) {
        stopSimulationTimer();
    } else {
        startSimulationTimer();
    }
    updateGameRenderingState();
}

function stopSimulationTimer(): void {
    const timer = gameState.generationTimer;
    gameState.generationTimer = null;
    if (timer) {
        clearInterval(timer);
    }
}

function showFloorCallPanel(floorNumber: number): void {
    if (!gameState.simulation || !gameState.missionConfig) return;
    const floorConfig = gameState.missionConfig.floors[floorNumber];
    if (!floorConfig) return;

    createLiftCallPanel(
        'floor',
        floorNumber,
        floorConfig.buttonType,
        gameState.simulation,
        gameState.missionConfig,
        () => { }
    );
}

function showLiftCallPanel(liftId: string): void {
    if (!gameState.simulation || !gameState.missionConfig) return;
    const liftState = gameState.simulation.building.lifts[liftId];
    if (!liftState) return;

    createLiftCallPanel(
        'lift',
        liftId,
        'NumberedFloors',
        gameState.simulation,
        gameState.missionConfig,
        () => { }
    );
}

function onLiftPanelClicked(liftId: string): void {
    showLiftCallPanel(liftId);
}

function onFloorPanelClicked(floorNumber: number): void {
    showFloorCallPanel(floorNumber);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function simulationLog(message: string, ...args: any[]): void {
    const generation = gameState.simulation?.generation || 0;
    // eslint-disable-next-line no-console
    console.log(generation + '> ' + message, ...args);

    const consoleOutput = document.getElementById('console-output');
    if (consoleOutput) {
        const line = document.createElement('div');
        line.className = 'console-line';

        const timestamp = document.createElement('span');
        timestamp.className = 'console-timestamp';
        timestamp.textContent = '[' + generation + '] ';

        const msgSpan = document.createElement('span');
        msgSpan.className = 'console-message';

        const formattedArgs = args.map(arg => {
            if (typeof arg === 'object') {
                try {
                    return JSON.stringify(arg, null, 2);
                } catch {
                    return '[Object]';
                }
            }
            return String(arg);
        });

        msgSpan.textContent = (message ? message + ' ' : '') + formattedArgs.join(' ');

        line.appendChild(timestamp);
        line.appendChild(msgSpan);
        consoleOutput.appendChild(line);

        // Auto-scroll to bottom
        consoleOutput.scrollTop = consoleOutput.scrollHeight;
    }
}

/**
 * Render the 3-panel layout
 */
function renderLayout(container: HTMLElement): void {
    container.innerHTML = `
    <div class="ui-chrome" id="ui-chrome"></div>
    <div class="building-panel" id="building-panel"></div>
    <div class="editor-container" style="display: flex; flex-direction: column; grid-column: 2; grid-row: 2; overflow: hidden;">
        <div class="editor-tabs-header">
            <button class="tab-btn" data-tab="documentation">${t('tabDocumentation')}</button>
            <button class="tab-btn active" data-tab="code">${t('tabCode')}</button>
            <button class="tab-btn" data-tab="snippets">${t('tabSnippets')}</button>
            <button class="tab-btn" data-tab="console">${t('tabConsole')}</button>
        </div>
        <div class="tab-content-container">
            <div class="editor-tab-content" id="tab-documentation">
                <div id="tab-documentation-panel" style="flex: 1;"></div>
            </div>
            <div class="editor-tab-content active" id="tab-code">
                <div class="editor-toolbar" id="editor-toolbar"></div>
                <div class="editor-panel" id="editor-panel" style="flex: 1;"></div>
            </div>
            <div class="editor-tab-content" id="tab-snippets">
                <div class="snippets-list" id="snippets-list"></div>
            </div>
            <div class="editor-tab-content" id="tab-console">
                <div id="console-output"></div>
            </div>
        </div>
    </div>
    <div class="bottom-panel" id="bottom-panel"></div>
  `;

    const chromeEl = document.getElementById('ui-chrome');
    if (chromeEl) {
        uiChrome = new UIChrome(
            chromeEl,
            {
                generation: gameState.simulation?.generation || 0,
                isRunning: gameState.simulation != null,
                isPaused: gameState.isPaused
            },
            {
                onStartStopSimulation: async (): Promise<void> => {
                    if (gameState.simulation != null) {
                        stopSimulation();
                    } else {
                        await startSimulation();
                    }
                },
                onTogglePause: (): void => {
                    togglePause();
                },
                onEndMission: (): void => {
                    endMission();
                }
            }
        );
    }
}

function setupEditorToolbar(container: HTMLElement, templateCode: string): void {
    container.innerHTML = `
        <button class="btn-save" id="btn-save" data-tooltip="${t('editorSaveTooltip')}">💾 ${t('editorSave')}</button>
        <button class="btn-load" id="btn-load" data-tooltip="${t('editorLoadTooltip')}">📂 ${t('editorLoad')}</button>
        <button class="btn-reset" id="btn-reset" data-tooltip="${t('editorResetTooltip')}">🔄 ${t('editorReset')}</button>
    `;

    const saveBtn = document.getElementById('btn-save') as HTMLButtonElement;
    const loadBtn = document.getElementById('btn-load') as HTMLButtonElement;
    const resetBtn = document.getElementById('btn-reset') as HTMLButtonElement;

    const updateLoadButtonState = (): void => {
        const hasSavedCode = getItem<string>(STORAGE_KEYS.USER_CODE) !== null;
        loadBtn.disabled = !hasSavedCode;
    };

    saveBtn.addEventListener('click', () => {
        const code = gameState.codeEditor?.getCode() || "";
        if (setItem(STORAGE_KEYS.USER_CODE, code)) {
            updateLoadButtonState();
            alert(t('editorSaveSuccess'));
        }
    });

    loadBtn.addEventListener('click', () => {
        const savedCode = getItem<string>(STORAGE_KEYS.USER_CODE);
        if (savedCode && confirm(t('editorLoadConfirm'))) {
            gameState.codeEditor?.setCode(savedCode);
        }
    });

    resetBtn.addEventListener('click', () => {
        if (confirm(t('editorResetConfirm'))) {
            gameState.codeEditor?.setCode(templateCode);
        }
    });

    updateLoadButtonState();
}

function setupEditorTabs(): void {
    const tabs = document.querySelectorAll('.tab-btn');
    const contents = document.querySelectorAll('.editor-tab-content');
    const snippetsList = document.getElementById('snippets-list');

    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const target = (tab as HTMLElement).dataset.tab;

            tabs.forEach(t => t.classList.remove('active'));
            contents.forEach(c => c.classList.remove('active'));

            tab.classList.add('active');
            document.getElementById(`tab-${target}`)?.classList.add('active');
        });
    });

    // Populate snippets
    if (snippetsList) {
        snippetsList.innerHTML = LUA_CODE_SNIPPETS.map(snippet => `
            <div class="snippet-item">
                <div class="snippet-header">
                    <span class="snippet-title">${snippet.title}</span>
                    <button class="btn-copy-snippet" data-code="${snippet.code.replace(/"/g, '&quot;')}">${t('copy')}</button>
                </div>
                <div class="snippet-desc">${snippet.description}</div>
                <div class="snippet-code-wrapper">
                    <pre class="snippet-code">${snippet.code.trim()}</pre>
                </div>
            </div>
        `).join('');

        snippetsList.querySelectorAll('.btn-copy-snippet').forEach(btn => {
            btn.addEventListener('click', () => {
                const code = (btn as HTMLElement).dataset.code;
                if (code) {
                    navigator.clipboard.writeText(code).then(() => {
                        const originalText = btn.textContent;
                        btn.textContent = t('copied');
                        setTimeout(() => {
                            btn.textContent = originalText;
                        }, 2000);
                    });
                }
            });
        });
    }
}

/**
 * End the mission and return to settings
 */
function endMission(): void {
    // Clean up Game Engine
    if (gameCtx) {
        gameCtx.registry.reset();
        gameCtx.destroy(/*removeCanvas*/true);
        gameCtx = null;
    }

    stopSimulation();

    gameState.phase = 'settings';
    gameState.missionConfig = null;
    uiChrome = null;
    gameState.codeEditor?.dispose();
    gameState.codeEditor = null;
    gameState.docEditor?.dispose();
    gameState.docEditor = null;

    const gameContainer = document.getElementById('game-container');
    if (gameContainer) {
        gameContainer.style.display = 'none';
        gameContainer.innerHTML = '';
    }

    const settingsContainer = document.getElementById('settings-container');
    if (settingsContainer) {
        settingsContainer.style.display = 'block';
        createMissionSettingsUI(settingsContainer, startMission);
    }
}

function updateGameRenderingState(): void {
    gameCtx?.registry.set('simulation', gameState?.simulation);
    uiChrome?.update({
        generation: gameState?.simulation?.generation || 0,
        isRunning: gameState.simulation != null,
        isPaused: gameState.isPaused
    });
}

function initGameRendering(missionConfig: MissionConfig): void {
    const buildingPanel = document.getElementById('building-panel');
    if (!buildingPanel) return;

    const phaserConfig: Phaser.Types.Core.GameConfig = {
        type: Phaser.CANVAS,
        width: buildingPanel.clientWidth,
        height: buildingPanel.clientHeight,
        parent: 'building-panel',
        canvasStyle: `display: block; width: 100%; height: 100%;`,
        backgroundColor: '#eeeeee',
        scene: [
            LiftSimulationScene
        ],
        input: {
            mouse: {
                preventDefaultWheel: false, // Allows page scrolling with mouse wheel
            }
        },
        fps: {
            forceSetTimeOut: true // Can help in iframe environments (itch.io)
        },
        callbacks: {
            postBoot: (game) => {
                // Ensures the game doesn't pause just because the iframe is weird
                game.events.on('hidden', () => { });
            }
        }
    };



    gameCtx = new Phaser.Game(phaserConfig);
    gameCtx.registry.set('missionConfig', missionConfig)

    // Handle window resize for the panels
    window.addEventListener('resize', (): void => {
        const buildingPanel = document.getElementById('building-panel');
        if (gameCtx && buildingPanel) {
            gameCtx.scale.resize(buildingPanel.clientWidth, buildingPanel.clientHeight);
        }
    });

    // Listen to Scene events
    EventBus.get().on(UIEvents.LIFT_PANEL_CLICKED, (data: LiftPanelClickedEvent) => {
        onLiftPanelClicked(data.liftId);
    });
    EventBus.get().on(UIEvents.FLOOR_PANEL_CLICKED, (data: FloorPanelClickedEvent) => {
        onFloorPanelClicked(data.floorNumber);
    });
    EventBus.get().on(UIEvents.LIFT_MOVE_COMPLETED, (data: LiftMoveCompletedEvent) => {
        gameState.eventQueue.push({ type: UIEvents.LIFT_MOVE_COMPLETED, data });
    });
    EventBus.get().on(SimulationEvents.SET_LIFT_TARGET, (data: SetLiftTargetEvent) => {
        gameState.eventQueue.push({ type: SimulationEvents.SET_LIFT_TARGET, data });
    });
}

function init(): void {
    // Check storage availability
    if (!isStorageAvailable()) {
        document.body.innerHTML = '<div style="color: red; padding: 20px;">Local storage is not available. The game requires local storage to save progress.</div>';
        return;
    }

    // Setup DOM structure
    const app = document.getElementById('app');
    if (!app) return;

    app.innerHTML = `
    <div id="settings-container"></div>
    <div id="game-container" style="display: none; width: 100%; height: 100%;"></div>
  `;

    // Show mission settings
    const settingsContainer = document.getElementById('settings-container');
    if (settingsContainer) {
        createMissionSettingsUI(settingsContainer, startMission);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dumpSimulation = (): void => {
        // eslint-disable-next-line no-console
        console.log('--- Simulation States ---');
        // eslint-disable-next-line no-console
        console.log('Mission Config:', gameState.missionConfig);
        // eslint-disable-next-line no-console
        console.log('Simulation State:', gameState.simulation);
        // eslint-disable-next-line no-console
        console.log('-------------------------');
    };
}

// Start the game when DOM is ready
async function bootstrap(): Promise<void> {
    // Wait for Monaco if loader is present
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).require === 'function') {
        await new Promise<void>((resolve) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).require(['vs/editor/editor.main'], () => {
                resolve();
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}


loadMonacoSubModules().then(() => {
    bootstrap();
});
